import React, { useEffect, useState } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Tabs,
    Progress,
    Space,
    Tag,
    Typography,
    Spin,
    message,
    Select,
    Button,
    Modal,
    Radio,
    Tooltip,
    Popconfirm,
    Input,
} from 'antd';
import ProjectCalendarModal from '../../components/ProjectCalendarModal';
import {
    DollarOutlined,
    TrophyOutlined,
    RiseOutlined,
    FallOutlined,
    ProjectOutlined,
    CalendarOutlined,
    FundOutlined,
    StarFilled,
    StarOutlined,
    SearchOutlined,
    EditOutlined,
    DownOutlined,
    UpOutlined,
} from '@ant-design/icons';
import { departmentAPI, projectAPI, financeDashboardAPI } from '../../services/api';
import { getAllocationRate, formatAmount, formatRate } from '../../utils/allocation';
import ProjectEditModal from '../projects/ProjectEditModal';

const { Title, Text } = Typography;
const { Option } = Select;

// 지출 카테고리 (project_budget_items 4종 + project_members 기반 내부인건비)
const EXPENSE_CATEGORIES = [
    { key: 'outsourcing', label: '외주비' },
    { key: 'material', label: '자재비' },
    { key: 'expense', label: '경비' },
    { key: 'other', label: '기타' },
    { key: 'labor', label: '내부인건비' },
];

// 수입/지출의 부서 귀속 기준 안내 문구
const EXPENSE_ATTRIBUTION_NOTE =
    '수입은 매출 귀속부서 배분율 기준, 지출은 등록 시 지정된 귀속부서 기준 전액입니다';

const createEmptyExpense = () =>
    EXPENSE_CATEGORIES.reduce((acc, { key }) => ({ ...acc, [key]: 0 }), {});

const sumExpense = (expense) =>
    EXPENSE_CATEGORIES.reduce((sum, { key }) => sum + (expense?.[key] || 0), 0);

/** 금액 표시 (0원은 '-'로 축약) */
const renderAmount = (amount, style) => {
    const value = parseFloat(amount) || 0;
    if (value === 0) return <Text type="secondary">-</Text>;
    return <Text style={style}>{formatAmount(value)}</Text>;
};

/** 수익 금액 표시 (적자는 빨간색), base가 있으면 수익률을 함께 표시 */
const renderProfit = (amount, base) => {
    const value = parseFloat(amount) || 0;
    const color = value > 0 ? '#52c41a' : value < 0 ? '#ff4d4f' : '#8c8c8c';
    const baseValue = parseFloat(base) || 0;

    return (
        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
            <Text strong style={{ color }}>{formatAmount(value)}</Text>
            {baseValue > 0 && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                    {formatRate(value / baseValue * 100)}
                </Text>
            )}
        </Space>
    );
};

/** 지출합계 셀 - 클릭하면 카테고리별 상세가 펼쳐진다 */
const renderExpenseTotalCell = (amount, expanded, onToggle) => (
    <Button
        type="link"
        size="small"
        onClick={onToggle}
        style={{ padding: 0, height: 'auto' }}
    >
        <Space size={4}>
            <Text strong style={{ color: (parseFloat(amount) || 0) === 0 ? '#8c8c8c' : '#fa541c' }}>
                {formatAmount(amount)}
            </Text>
            {expanded ? <UpOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
        </Space>
    </Button>
);

/** 지출 카테고리별 상세 (펼침 영역) */
const renderExpenseDetail = (expense) => (
    <Row gutter={[16, 8]} style={{ padding: '4px 8px' }}>
        {EXPENSE_CATEGORIES.map(({ key, label }) => (
            <Col key={key} xs={12} sm={8} md={4}>
                <Statistic
                    title={label}
                    value={Math.round(expense?.[key] || 0)}
                    suffix="원"
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    valueStyle={{ fontSize: 14, color: (expense?.[key] || 0) === 0 ? '#bfbfbf' : '#595959' }}
                />
            </Col>
        ))}
        <Col xs={12} sm={8} md={4}>
            <Statistic
                title="지출합계"
                value={Math.round(sumExpense(expense))}
                suffix="원"
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                valueStyle={{ fontSize: 14, color: '#fa541c', fontWeight: 600 }}
            />
        </Col>
    </Row>
);

/** 펼침 상태 토글 */
const toggleKey = (keys, key) =>
    keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key];

const StrategicMeetingPage = () => {
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [departmentStats, setDepartmentStats] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [activeTabKey, setActiveTabKey] = useState('overview');
    const [currentYear] = useState(new Date().getFullYear());
    const [descriptionModal, setDescriptionModal] = useState({
        visible: false,
        title: '',
        content: ''
    });
    const [priorityFilter, setPriorityFilter] = useState('all'); // 'all', 'priority', 'normal'
    const [projectCalendarVisible, setProjectCalendarVisible] = useState(false);
    const [searchText, setSearchText] = useState(''); // 프로젝트명 검색
    // 프로젝트 수정 모달 관련 state
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    // 지출 상세(외주비/자재비/경비/기타/내부인건비) 펼침 상태
    const [showOverallExpenseDetail, setShowOverallExpenseDetail] = useState(false);
    const [expandedDeptExpenseCards, setExpandedDeptExpenseCards] = useState([]);
    const [expandedProfitRows, setExpandedProfitRows] = useState([]);
    const [expandedProjectRows, setExpandedProjectRows] = useState([]);

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear]);

    // 프로젝트 설명 모달 핸들러
    const showDescriptionModal = (projectName, description) => {
        setDescriptionModal({
            visible: true,
            title: projectName,
            content: description || '프로젝트 설명이 없습니다.'
        });
    };

    const handleDescriptionModalClose = () => {
        setDescriptionModal({
            visible: false,
            title: '',
            content: ''
        });
    };

    // 중점관리 설정/해제 함수
    const togglePriority = async (projectId, isPriority) => {
        try {
            console.log('🌟 중점관리 설정 요청:', { projectId, isPriority });
            const response = await projectAPI.update(projectId, { is_priority: isPriority });
            console.log('🌟 중점관리 설정 응답:', response);
            message.success(isPriority ? '중점관리 프로젝트로 설정되었습니다.' : '중점관리에서 해제되었습니다.');
            fetchData(); // 데이터 새로고침
        } catch (error) {
            console.error('🚨 중점관리 설정 오류:', error);
            message.error('중점관리 설정 중 오류가 발생했습니다.');
        }
    };

    // 프로젝트 수정 핸들러
    const handleEditProject = (projectId) => {
        setSelectedProjectId(projectId);
        setEditModalVisible(true);
    };

    // 프로젝트 수정 성공 핸들러
    const handleEditSuccess = () => {
        setEditModalVisible(false);
        setSelectedProjectId(null);
        fetchData(); // 데이터 새로고침
    };

    // 프로젝트 수정 취소 핸들러
    const handleEditCancel = () => {
        setEditModalVisible(false);
        setSelectedProjectId(null);
    };

    // 우선순위 및 검색어 필터링 함수
    const filterProjectsByPriority = (projects) => {
        let filteredProjects = projects;

        // 우선순위 필터링
        if (priorityFilter === 'priority') {
            filteredProjects = filteredProjects.filter(p => p.is_priority);
        } else if (priorityFilter === 'normal') {
            filteredProjects = filteredProjects.filter(p => !p.is_priority);
        }

        // 검색어 필터링
        if (searchText) {
            filteredProjects = filteredProjects.filter(p => 
                p.name.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        return filteredProjects;
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [deptResponse, projectResponse, allProjectsResponse, costResponse] = await Promise.all([
                departmentAPI.getAll({ is_active: 'true' }),
                projectAPI.getAll({ year: selectedYear }),
                projectAPI.getAll(), // 모든 프로젝트 조회해서 실제 데이터 확인
                // 지출 집계는 권한/오류로 실패해도 나머지 현황은 보여준다
                financeDashboardAPI.getProjectCosts({ year: selectedYear }).catch(error => {
                    console.warn('⚠️ 프로젝트별 수입/지출 집계 조회 실패:', error);
                    return { data: { costs: [], incomes: [] } };
                })
            ]);

            const departmentsData = deptResponse.data.departments || [];
            const projectsData = projectResponse.data.projects || [];
            const allProjectsData = allProjectsResponse.data.projects || [];

            // 프로젝트 × 귀속부서별 지출을 "프로젝트ID:부서ID" 키로 매핑
            // (지출은 배분율 안분 없이 등록 시 지정된 귀속부서에 전액 귀속된다)
            const costMap = new Map(
                (costResponse.data.costs || []).map(cost => [
                    `${Number(cost.project_id)}:${Number(cost.department_id)}`,
                    cost
                ])
            );
            // 실제 수입금액은 프로젝트 단위로 내려오며 매출 귀속부서 배분율로 안분한다
            const incomeMap = new Map(
                (costResponse.data.incomes || []).map(income => [
                    Number(income.project_id),
                    parseFloat(income.amount) || 0
                ])
            );
            console.log('💸 지출 집계 (프로젝트×부서) 건수:', costMap.size, '/ 수입 집계 프로젝트 수:', incomeMap.size);

            console.log('=== 전략회의 데이터 조회 ===');
            console.log('부서 수:', departmentsData.length);
            console.log('부서 목록:', departmentsData.map(d => ({ id: d.id, name: d.name })));
            
            // 부서 중복 체크
            const departmentIds = departmentsData.map(d => d.id);
            const uniqueDepartmentIds = [...new Set(departmentIds)];
            if (departmentIds.length !== uniqueDepartmentIds.length) {
                console.warn('⚠️ 부서 데이터에 중복이 있습니다!');
                console.log('전체 부서 ID:', departmentIds);
                console.log('고유 부서 ID:', uniqueDepartmentIds);
                
                // 중복 제거
                const uniqueDepartments = departmentsData.filter((dept, index, self) => 
                    self.findIndex(d => d.id === dept.id) === index
                );
                console.log('중복 제거 후 부서 수:', uniqueDepartments.length);
                setDepartments(uniqueDepartments);
            } else {
                setDepartments(departmentsData);
            }
            
            console.log(`${selectedYear}년 프로젝트 수:`, projectsData.length);
            console.log('전체 프로젝트 수:', allProjectsData.length);
            
            if (projectsData.length > 0) {
                console.log('프로젝트 부서 매칭 정보:', projectsData.map(p => ({ 
                    name: p.name, 
                    status: p.status,
                    primary_department_id: p.primary_department_id,
                    contract_date: p.contract_date,
                    contract_amount: p.contract_amount,
                    is_priority: p.is_priority  // 중점관리 필드 확인
                })));
                
                // 중점관리 프로젝트만 별도 확인
                const priorityProjects = projectsData.filter(p => p.is_priority);
                console.log('🌟 중점관리 프로젝트:', priorityProjects.map(p => ({ 
                    name: p.name,
                    is_priority: p.is_priority
                })));
                
                // 계획단계 프로젝트만 별도 확인
                const allPlanningProjects = projectsData.filter(p => p.status === 'planning');
                console.log('전체 계획단계 프로젝트:', allPlanningProjects.map(p => ({ 
                    name: p.name,
                    dept_id: p.primary_department_id,
                    amount: p.contract_amount
                })));
            }
            

            // 부서별 통계 계산 (중복 제거된 부서 데이터 사용)
            const finalDepartments = departmentIds.length !== uniqueDepartmentIds.length 
                ? departmentsData.filter((dept, index, self) => self.findIndex(d => d.id === dept.id) === index)
                : departmentsData;
                
            await calculateDepartmentStats(finalDepartments, projectsData, costMap, incomeMap);
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            message.error('데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const calculateDepartmentStats = async (
        departmentsData,
        projectsData,
        costMap = new Map(),
        incomeMap = new Map()
    ) => {
        console.log('📊 부서별 통계 계산 시작:', {
            departments: departmentsData.map(d => ({id: d.id, name: d.name})),
            projects: projectsData.length
        });
        
        // 부서 처리 순서 미리 확인
        console.log('🔍 부서 처리 예정 순서:', departmentsData.map((d, index) => 
            `${index + 1}. ${d.name} (ID: ${d.id})`
        ));
        
        const stats = [];
        
        for (let i = 0; i < departmentsData.length; i++) {
            const department = departmentsData[i];
            try {
                console.log(`🏢 [${i + 1}/${departmentsData.length}] ${department.name} (ID: ${department.id}) 처리 시작`);
                
                // 이미 처리된 부서인지 확인
                const alreadyProcessed = stats.find(s => s.id === department.id);
                if (alreadyProcessed) {
                    console.log(`⚠️ ${department.name} (ID: ${department.id}) 이미 처리됨, 건너뜀`);
                    continue;
                }
                
                // 부서 예산 정보 조회
                const budgetResponse = await departmentAPI.getBudgets(department.id, { year: selectedYear });
                const budgets = budgetResponse.data.budgets || [];
                
                console.log(`💰 ${department.name} 예산 응답:`, budgets.length > 0 ? '있음' : '없음');
                const currentBudget = budgets.find(b => b.year === selectedYear);

                // 프로젝트 1건을 해당 부서 관점으로 변환
                //  - 매출금액(계약금액) / 수입금액(실제 입금): 매출 귀속부서 배분율만큼 안분
                //  - 지출: 지출 등록 시 지정된 귀속부서 기준 전액 (배분율 미적용)
                const toDepartmentProject = (p, rate) => {
                    const projectCost = costMap.get(`${Number(p.id)}:${Number(department.id)}`);
                    const departmentExpense = EXPENSE_CATEGORIES.reduce((acc, { key }) => ({
                        ...acc,
                        [key]: parseFloat(projectCost?.[key]) || 0
                    }), {});
                    const allocatedContractAmount = parseFloat(p.contract_amount || 0) * rate / 100;
                    const allocatedIncomeAmount = (incomeMap.get(Number(p.id)) || 0) * rate / 100;
                    const totalDepartmentExpense = sumExpense(departmentExpense);

                    return {
                        ...p,
                        allocation_rate_for_department: rate,
                        allocated_contract_amount: allocatedContractAmount,
                        allocated_income_amount: allocatedIncomeAmount,
                        department_expense: departmentExpense,
                        department_total_expense: totalDepartmentExpense,
                        // 수익: 매출금액 대비 / 실제 수입금액 대비 두 가지로 구분
                        department_profit: allocatedContractAmount - totalDepartmentExpense,
                        department_income_profit: allocatedIncomeAmount - totalDepartmentExpense
                    };
                };

                const hasDepartmentExpense = (projectId) => {
                    const cost = costMap.get(`${Number(projectId)}:${Number(department.id)}`);
                    return !!cost && (parseFloat(cost.total) || 0) !== 0;
                };

                // 수주 지표는 기존과 동일하게 해당 부서에 매출이 배분된 프로젝트 기준
                const departmentProjects = projectsData
                    .map(p => {
                        const rate = getAllocationRate(p, department.id);
                        if (rate <= 0) return null;

                        return toDepartmentProject(p, rate);
                    })
                    .filter(Boolean);

                console.log(`📋 ${department.name} 프로젝트: ${departmentProjects.length}개`,
                    departmentProjects.map(p => `${p.name}(${p.code}, ${p.allocation_rate_for_department}%)`));

                // 수주추진금액 (계획단계 프로젝트) - 매출 귀속부서 배분율 적용
                const planningAmount = departmentProjects
                    .filter(p => p.status === 'planning')
                    .reduce((sum, p) => sum + p.allocated_contract_amount, 0);

                // 매출금액 (진행중/완료 프로젝트) - 매출 귀속부서 배분율 적용
                const revenueAmount = departmentProjects
                    .filter(p => ['in_progress', 'completed'].includes(p.status))
                    .reduce((sum, p) => sum + p.allocated_contract_amount, 0);

                // 지출/수익 (수입 기준과 동일하게 진행중/완료 프로젝트만 집계)
                // 매출 배분은 없지만 이 부서로 지출이 등록된 프로젝트도 지출 현황에 포함한다
                const expenseOnlyProjects = projectsData
                    .filter(p => ['in_progress', 'completed'].includes(p.status))
                    .filter(p => getAllocationRate(p, department.id) <= 0)
                    .filter(p => hasDepartmentExpense(p.id))
                    .map(p => toDepartmentProject(p, 0));

                const activeProjects = [
                    ...departmentProjects.filter(p => ['in_progress', 'completed'].includes(p.status)),
                    ...expenseOnlyProjects
                ];

                if (expenseOnlyProjects.length > 0) {
                    console.log(`💸 ${department.name} 매출 배분 없이 지출만 등록된 프로젝트: ${expenseOnlyProjects.length}개`,
                        expenseOnlyProjects.map(p => p.name));
                }

                const expenseByCategory = activeProjects.reduce((acc, p) => {
                    EXPENSE_CATEGORIES.forEach(({ key }) => {
                        acc[key] += p.department_expense[key];
                    });
                    return acc;
                }, createEmptyExpense());

                // 실제 수입금액 (프로젝트 수입/지출에 등록된 입금액, 매출 배분율 안분)
                const incomeAmount = activeProjects
                    .reduce((sum, p) => sum + p.allocated_income_amount, 0);

                const totalExpense = sumExpense(expenseByCategory);
                // 수익은 매출금액 대비 / 실제 수입금액 대비 두 가지로 구분한다 (수익률은 표시 시점에 계산)
                const profitAmount = revenueAmount - totalExpense;
                const incomeProfitAmount = incomeAmount - totalExpense;

                console.log(`${department.name} - 계획: ${planningAmount.toLocaleString()}, 매출: ${revenueAmount.toLocaleString()}, 수입: ${incomeAmount.toLocaleString()}, 지출: ${totalExpense.toLocaleString()}, 수익(매출대비): ${profitAmount.toLocaleString()}, 수익(수입대비): ${incomeProfitAmount.toLocaleString()}`);

                const orderTarget = parseFloat(currentBudget?.order_target || 0);
                const annualBudget = parseFloat(currentBudget?.annual_budget || 0);

                // 달성률 계산
                const plannedAchievementRate = orderTarget > 0 
                    ? ((planningAmount + revenueAmount) / orderTarget * 100).toFixed(1)
                    : 0;
                
                const actualAchievementRate = orderTarget > 0 
                    ? (revenueAmount / orderTarget * 100).toFixed(1)
                    : 0;

                const departmentStatData = {
                    id: department.id,
                    name: department.name,
                    code: department.code,
                    orderTarget,
                    annualBudget,
                    planningAmount,
                    revenueAmount,
                    incomeAmount,
                    expenseByCategory,
                    totalExpense,
                    profitAmount,
                    incomeProfitAmount,
                    plannedAchievementRate: parseFloat(plannedAchievementRate),
                    actualAchievementRate: parseFloat(actualAchievementRate),
                    projectCount: departmentProjects.length,
                    planningProjects: departmentProjects.filter(p => p.status === 'planning'),
                    activeProjects
                };
                
                console.log(`✅ ${department.name} 통계 완료:`, {
                    id: departmentStatData.id,
                    name: departmentStatData.name,
                    projectCount: departmentStatData.projectCount,
                    orderTarget,
                    planningAmount,
                    revenueAmount
                });
                
                stats.push(departmentStatData);
            } catch (error) {
                console.error(`❌ 부서 ${department.name} (ID: ${department.id}) 통계 계산 실패:`, error);
                console.error(`실패 상세 정보:`, {
                    departmentId: department.id,
                    departmentName: department.name,
                    errorMessage: error.message,
                    errorStack: error.stack
                });
                
                // 에러가 발생해도 기본값으로라도 부서를 추가
                const fallbackData = {
                    id: department.id,
                    name: department.name,
                    code: department.code,
                    orderTarget: 0,
                    annualBudget: 0,
                    planningAmount: 0,
                    revenueAmount: 0,
                    incomeAmount: 0,
                    expenseByCategory: createEmptyExpense(),
                    totalExpense: 0,
                    profitAmount: 0,
                    incomeProfitAmount: 0,
                    plannedAchievementRate: 0,
                    actualAchievementRate: 0,
                    projectCount: 0,
                    planningProjects: [],
                    activeProjects: []
                };
                
                console.log(`🚨 ${department.name} 폴백 데이터로 추가`);
                stats.push(fallbackData);
            }
        }

        console.log('📋 최종 부서별 통계:', stats.map(s => ({id: s.id, name: s.name})));
        console.log('🎯 통계 계산 완료, 상태 업데이트 중...');
        setDepartmentStats(stats);
        console.log('✅ 부서별 통계 상태 업데이트 완료');
    };

    // 전체 통계 계산
    const overallStats = {
        totalOrderTarget: departmentStats.reduce((sum, dept) => sum + dept.orderTarget, 0),
        totalPlanningAmount: departmentStats.reduce((sum, dept) => sum + dept.planningAmount, 0),
        totalRevenueAmount: departmentStats.reduce((sum, dept) => sum + dept.revenueAmount, 0),
        totalBudget: departmentStats.reduce((sum, dept) => sum + dept.annualBudget, 0),
        totalIncomeAmount: departmentStats.reduce((sum, dept) => sum + dept.incomeAmount, 0),
        // 전체 지출 (카테고리별 합계)
        totalExpenseByCategory: departmentStats.reduce((acc, dept) => {
            EXPENSE_CATEGORIES.forEach(({ key }) => {
                acc[key] += dept.expenseByCategory?.[key] || 0;
            });
            return acc;
        }, createEmptyExpense()),
    };

    overallStats.totalExpense = sumExpense(overallStats.totalExpenseByCategory);
    // 수익: 매출금액 대비 / 실제 수입금액 대비
    overallStats.totalProfit = overallStats.totalRevenueAmount - overallStats.totalExpense;
    overallStats.overallProfitRate = overallStats.totalRevenueAmount > 0
        ? (overallStats.totalProfit / overallStats.totalRevenueAmount * 100)
        : 0;
    overallStats.totalIncomeProfit = overallStats.totalIncomeAmount - overallStats.totalExpense;
    overallStats.overallIncomeProfitRate = overallStats.totalIncomeAmount > 0
        ? (overallStats.totalIncomeProfit / overallStats.totalIncomeAmount * 100)
        : 0;

    overallStats.overallPlannedRate = overallStats.totalOrderTarget > 0 
        ? ((overallStats.totalPlanningAmount + overallStats.totalRevenueAmount) / overallStats.totalOrderTarget * 100).toFixed(1)
        : 0;
    
    overallStats.overallActualRate = overallStats.totalOrderTarget > 0 
        ? (overallStats.totalRevenueAmount / overallStats.totalOrderTarget * 100).toFixed(1)
        : 0;

    // 전체 현황 테이블 컬럼
    const overviewColumns = [
        {
            title: '부서',
            dataIndex: 'name',
            key: 'name',
            width: 120,
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <strong>{name}</strong>
                    <Text type="secondary" style={{ fontSize: 11 }}>{record.code}</Text>
                </Space>
            )
        },
        {
            title: '수주목표금액',
            dataIndex: 'orderTarget',
            key: 'orderTarget',
            width: 140,
            align: 'right',
            render: amount => (
                <Text strong style={{ color: '#1890ff' }}>
                    {amount.toLocaleString()}원
                </Text>
            )
        },
        {
            title: '수주추진금액',
            dataIndex: 'planningAmount',
            key: 'planningAmount',
            width: 140,
            align: 'right',
            render: amount => (
                <Text style={{ color: '#faad14' }}>
                    {amount.toLocaleString()}원
                </Text>
            )
        },
        {
            title: '매출금액',
            dataIndex: 'revenueAmount',
            key: 'revenueAmount',
            width: 140,
            align: 'right',
            render: amount => (
                <Text style={{ color: '#52c41a' }}>
                    {amount.toLocaleString()}원
                </Text>
            )
        },
        {
            title: '계획 수주달성률',
            dataIndex: 'plannedAchievementRate',
            key: 'plannedAchievementRate',
            width: 160,
            align: 'center',
            render: rate => {
                const color = rate >= 100 ? '#52c41a' : rate >= 80 ? '#faad14' : '#ff4d4f';
                return (
                    <Space direction="vertical" size={0}>
                        <Progress 
                            percent={Math.min(rate, 100)} 
                            size="small"
                            strokeColor={color}
                            showInfo={false}
                        />
                        <Text strong style={{ color }}>
                            {rate}%
                        </Text>
                    </Space>
                );
            }
        },
        {
            title: '실제 수주달성률',
            dataIndex: 'actualAchievementRate',
            key: 'actualAchievementRate',
            width: 160,
            align: 'center',
            render: rate => {
                const color = rate >= 100 ? '#52c41a' : rate >= 80 ? '#faad14' : '#ff4d4f';
                return (
                    <Space direction="vertical" size={0}>
                        <Progress 
                            percent={Math.min(rate, 100)} 
                            size="small"
                            strokeColor={color}
                            showInfo={false}
                        />
                        <Text strong style={{ color }}>
                            {rate}%
                        </Text>
                    </Space>
                );
            }
        },
        {
            title: '프로젝트 수',
            dataIndex: 'projectCount',
            key: 'projectCount',
            width: 100,
            align: 'center',
            render: count => <Tag color="blue">{count}개</Tag>
        }
    ];

    // 부서별 수익 현황 테이블 컬럼
    const profitColumns = [
        {
            title: '부서',
            dataIndex: 'name',
            key: 'name',
            width: 120,
            fixed: 'left',
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <strong>{name}</strong>
                    <Text type="secondary" style={{ fontSize: 11 }}>{record.code}</Text>
                </Space>
            )
        },
        {
            title: '매출금액',
            dataIndex: 'revenueAmount',
            key: 'revenueAmount',
            width: 150,
            align: 'right',
            sorter: (a, b) => a.revenueAmount - b.revenueAmount,
            showSorterTooltip: false,
            render: amount => renderAmount(amount, { color: '#52c41a', fontWeight: 600 })
        },
        {
            title: '수입금액',
            dataIndex: 'incomeAmount',
            key: 'incomeAmount',
            width: 150,
            align: 'right',
            sorter: (a, b) => a.incomeAmount - b.incomeAmount,
            showSorterTooltip: false,
            render: amount => renderAmount(amount, { color: '#13c2c2', fontWeight: 600 })
        },
        {
            title: '지출합계',
            dataIndex: 'totalExpense',
            key: 'totalExpense',
            width: 170,
            align: 'right',
            sorter: (a, b) => a.totalExpense - b.totalExpense,
            showSorterTooltip: false,
            render: (amount, record) => renderExpenseTotalCell(
                amount,
                expandedProfitRows.includes(record.id),
                () => setExpandedProfitRows(keys => toggleKey(keys, record.id))
            )
        },
        {
            title: '수익 (매출금액 대비)',
            dataIndex: 'profitAmount',
            key: 'profitAmount',
            width: 160,
            align: 'right',
            sorter: (a, b) => a.profitAmount - b.profitAmount,
            showSorterTooltip: false,
            render: (amount, record) => renderProfit(amount, record.revenueAmount)
        },
        {
            title: '수익 (수입금액 대비)',
            dataIndex: 'incomeProfitAmount',
            key: 'incomeProfitAmount',
            width: 160,
            align: 'right',
            sorter: (a, b) => a.incomeProfitAmount - b.incomeProfitAmount,
            showSorterTooltip: false,
            render: (amount, record) => renderProfit(amount, record.incomeAmount)
        }
    ];

    // 부서별 수익 현황 합계 행
    const renderProfitSummary = (rows) => {
        const totalRevenue = rows.reduce((sum, r) => sum + (r.revenueAmount || 0), 0);
        const totalIncome = rows.reduce((sum, r) => sum + (r.incomeAmount || 0), 0);
        const totalExpense = rows.reduce((sum, r) => sum + (r.totalExpense || 0), 0);

        return (
            <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                    <Table.Summary.Cell index={0}><strong>합계</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                        {renderAmount(totalRevenue, { color: '#52c41a', fontWeight: 600 })}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                        {renderAmount(totalIncome, { color: '#13c2c2', fontWeight: 600 })}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                        {renderAmount(totalExpense, { color: '#fa541c', fontWeight: 600 })}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                        {renderProfit(totalRevenue - totalExpense, totalRevenue)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                        {renderProfit(totalIncome - totalExpense, totalIncome)}
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    // 부서별 탭 생성
    const createDepartmentTab = (department) => {
        console.log('🔍 부서 탭 생성 시도:', {
            찾는부서: `${department.name} (ID: ${department.id})`,
            보유통계: departmentStats.length,
            통계부서목록: departmentStats.map(s => `${s.name}(${s.id})`).join(', ')
        });
        
        const departmentStat = departmentStats.find(stat => stat.id === department.id);
        if (!departmentStat) {
            console.warn(`❌ ${department.name} 통계 데이터 없음`);
            return (
                <div style={{ padding: '50px', textAlign: 'center' }}>
                    <Text type="secondary">
                        {department.name}의 데이터를 찾을 수 없습니다
                    </Text>
                    <div style={{ marginTop: 16 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            찾는 부서: {department.name} (ID: {department.id})
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                통계 보유 부서: {departmentStats.map(s => `${s.name}(${s.id})`).join(', ')}
                            </Text>
                        </div>
                    </div>
                </div>
            );
        }
        
        console.log(`✅ ${department.name} 탭 생성 성공`);

        // 프로젝트 테이블 컬럼
        const projectColumns = [
            {
                title: '프로젝트명',
                dataIndex: 'name',
                key: 'name',
                width: 180,
                sorter: (a, b) => a.name.localeCompare(b.name),
                showSorterTooltip: false,
                render: (name, record) => (
                    <Space direction="vertical" size={0}>
                        <Space align="center" size={4}>
                            {record.is_priority && (
                                <StarFilled style={{ color: '#ff4d4f', fontSize: 14 }} />
                            )}
                            <strong>{name}</strong>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 11 }}>{record.code}</Text>
                    </Space>
                )
            },
            {
                title: '고객사',
                dataIndex: 'client_name',
                key: 'client_name',
                width: 120,
                sorter: (a, b) => (a.client_name || '').localeCompare(b.client_name || ''),
                showSorterTooltip: false,
                render: (client_name) => client_name || '-'
            },
            {
                title: '계약금액',
                dataIndex: 'contract_amount',
                key: 'contract_amount',
                width: 170,
                align: 'right',
                sorter: (a, b) => (a.allocated_contract_amount || 0) - (b.allocated_contract_amount || 0),
                showSorterTooltip: false,
                // 매출이 여러 부서로 분할된 경우 전체금액과 해당 부서 배분금액/배분율을 함께 표시
                render: (amount, record) => {
                    const totalAmount = parseFloat(amount || 0);
                    const rate = record.allocation_rate_for_department ?? 100;

                    // 매출 배분 없이 지출만 등록된 프로젝트
                    if (rate <= 0) {
                        return (
                            <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {formatAmount(totalAmount)}
                                </Text>
                                <Tooltip title="이 부서에 배분된 매출은 없고 지출만 등록된 프로젝트입니다">
                                    <Tag color="default" style={{ margin: 0 }}>매출배분 없음</Tag>
                                </Tooltip>
                            </Space>
                        );
                    }

                    if (rate >= 100) {
                        return (
                            <Text strong style={{ color: '#52c41a' }}>
                                {formatAmount(totalAmount)}
                            </Text>
                        );
                    }

                    return (
                        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {formatAmount(totalAmount)}
                            </Text>
                            <Text strong style={{ color: '#52c41a' }}>
                                ({formatAmount(record.allocated_contract_amount)} - {formatRate(rate)})
                            </Text>
                        </Space>
                    );
                }
            },
            {
                title: 'PM',
                dataIndex: ['pm', 'name'],
                key: 'pm',
                width: 80,
                sorter: (a, b) => (a.pm?.name || '').localeCompare(b.pm?.name || ''),
                showSorterTooltip: false,
                render: (name) => name || '-'
            },
            {
                title: '계약(예정)일',
                dataIndex: 'contract_date',
                key: 'contract_date',
                width: 90,
                sorter: (a, b) => new Date(a.contract_date || 0) - new Date(b.contract_date || 0),
                showSorterTooltip: false,
                render: date => date ? new Date(date).toLocaleDateString('ko-KR', { 
                    year: '2-digit', month: '2-digit', day: '2-digit' 
                }) : '-'
            },
            {
                title: '시작일',
                dataIndex: 'start_date',
                key: 'start_date',
                width: 90,
                sorter: (a, b) => new Date(a.start_date || 0) - new Date(b.start_date || 0),
                showSorterTooltip: false,
                render: date => date ? new Date(date).toLocaleDateString('ko-KR', { 
                    year: '2-digit', month: '2-digit', day: '2-digit' 
                }) : '-'
            },
            {
                title: '종료일',
                dataIndex: 'end_date',
                key: 'end_date',
                width: 90,
                sorter: (a, b) => new Date(a.end_date || 0) - new Date(b.end_date || 0),
                showSorterTooltip: false,
                render: date => date ? new Date(date).toLocaleDateString('ko-KR', { 
                    year: '2-digit', month: '2-digit', day: '2-digit' 
                }) : '-'
            },
            {
                title: '설명',
                dataIndex: 'description',
                key: 'description',
                width: 120,
                render: (description, record) => {
                    if (!description) return '-';
                    
                    const shortDesc = description.length > 20 
                        ? `${description.substring(0, 20)}...` 
                        : description;
                    
                    return (
                        <Button 
                            type="link" 
                            size="small"
                            onClick={() => showDescriptionModal(record.name, description)}
                            style={{ padding: 0, height: 'auto', textAlign: 'left' }}
                        >
                            {shortDesc}
                        </Button>
                    );
                }
            },
            {
                title: '상태',
                dataIndex: 'status',
                key: 'status',
                width: 80,
                align: 'center',
                sorter: (a, b) => {
                    const statusOrder = { planning: 1, in_progress: 2, completed: 3, cancelled: 4 };
                    return (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5);
                },
                showSorterTooltip: false,
                render: status => {
                    const statusMap = {
                        planning: { text: '계획', color: 'orange' },
                        in_progress: { text: '진행중', color: 'blue' },
                        completed: { text: '완료', color: 'green' },
                        cancelled: { text: '취소', color: 'red' }
                    };
                    const statusInfo = statusMap[status] || { text: status, color: 'default' };
                    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
                }
            },
            {
                title: '중점관리',
                dataIndex: 'is_priority',
                key: 'is_priority',
                width: 80,
                align: 'center',
                sorter: (a, b) => (b.is_priority ? 1 : 0) - (a.is_priority ? 1 : 0),
                showSorterTooltip: false,
                render: (is_priority, record) => (
                    <Tooltip title={is_priority ? '중점관리 해제' : '중점관리 설정'}>
                        <Popconfirm
                            title={`${record.name}을(를) ${is_priority ? '중점관리에서 해제' : '중점관리로 설정'}하시겠습니까?`}
                            onConfirm={() => togglePriority(record.id, !is_priority)}
                            okText="확인"
                            cancelText="취소"
                        >
                            <Button
                                type="text"
                                icon={is_priority ? 
                                    <StarFilled style={{ color: '#ff4d4f' }} /> : 
                                    <StarOutlined style={{ color: '#d9d9d9' }} />
                                }
                                size="small"
                            />
                        </Popconfirm>
                    </Tooltip>
                )
            },
            {
                title: '공정관리',
                key: 'flow_management',
                width: 80,
                align: 'center',
                render: (_, record) => {
                    if (record.flow_link) {
                        return (
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => window.open(record.flow_link, '_blank')}
                                style={{
                                    backgroundColor: '#52c41a',
                                    borderColor: '#52c41a',
                                }}
                            >
                                플로우
                            </Button>
                        );
                    } else {
                        return <Text type="secondary">미설정</Text>;
                    }
                }
            },
            {
                title: '수정',
                key: 'edit',
                width: 60,
                align: 'center',
                render: (_, record) => (
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEditProject(record.id)}
                        style={{ color: '#1890ff' }}
                    />
                )
            }
        ];

        // 진행사업 탭 전용 행 키 (부서 탭 간 펼침 상태가 섞이지 않도록 부서 ID를 포함)
        const activeRowKey = record => `${department.id}-${record.id}`;

        const expenseDetailVisible = expandedDeptExpenseCards.includes(department.id);

        // 진행사업 탭 전용: 계약금액 뒤에 수입금액/지출/수익 컬럼 추가
        const costColumns = [
            {
                title: '수입금액',
                dataIndex: 'allocated_income_amount',
                key: 'allocated_income_amount',
                width: 150,
                align: 'right',
                sorter: (a, b) => (a.allocated_income_amount || 0) - (b.allocated_income_amount || 0),
                showSorterTooltip: false,
                render: amount => renderAmount(amount, { color: '#13c2c2', fontWeight: 600 })
            },
            {
                title: '지출합계',
                dataIndex: 'department_total_expense',
                key: 'department_total_expense',
                width: 170,
                align: 'right',
                sorter: (a, b) => (a.department_total_expense || 0) - (b.department_total_expense || 0),
                showSorterTooltip: false,
                render: (amount, record) => renderExpenseTotalCell(
                    amount,
                    expandedProjectRows.includes(activeRowKey(record)),
                    () => setExpandedProjectRows(keys => toggleKey(keys, activeRowKey(record)))
                )
            },
            {
                title: '수익 (매출금액 대비)',
                dataIndex: 'department_profit',
                key: 'department_profit',
                width: 160,
                align: 'right',
                sorter: (a, b) => (a.department_profit || 0) - (b.department_profit || 0),
                showSorterTooltip: false,
                render: (amount, record) => renderProfit(amount, record.allocated_contract_amount)
            },
            {
                title: '수익 (수입금액 대비)',
                dataIndex: 'department_income_profit',
                key: 'department_income_profit',
                width: 160,
                align: 'right',
                sorter: (a, b) => (a.department_income_profit || 0) - (b.department_income_profit || 0),
                showSorterTooltip: false,
                render: (amount, record) => renderProfit(amount, record.allocated_income_amount)
            }
        ];

        const contractColumnIndex = projectColumns.findIndex(col => col.key === 'contract_amount');
        const activeProjectColumns = [
            // 표가 넓어지므로 프로젝트명은 좌측 고정
            ...projectColumns
                .slice(0, contractColumnIndex + 1)
                .map((col, i) => (i === 0 ? { ...col, fixed: 'left' } : col)),
            ...costColumns,
            ...projectColumns.slice(contractColumnIndex + 1)
        ];

        // 진행사업 합계 행 (계약금액/수입금액/지출/수익 열만 값 표시)
        const renderActiveProjectSummary = (rows) => {
            if (rows.length === 0) return null;

            const totalRevenue = rows.reduce((sum, r) => sum + (r.allocated_contract_amount || 0), 0);
            const totalIncome = rows.reduce((sum, r) => sum + (r.allocated_income_amount || 0), 0);
            const totalExpense = rows.reduce((sum, r) => sum + (r.department_total_expense || 0), 0);
            // 합계 행 뒤쪽(PM~수정)은 빈 셀로 채운다
            const trailingCellCount = projectColumns.length - (contractColumnIndex + 1);

            return (
                <Table.Summary fixed>
                    <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                        <Table.Summary.Cell index={0} colSpan={contractColumnIndex}>
                            <strong>합계 ({rows.length}건)</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                            {renderAmount(totalRevenue, { color: '#52c41a', fontWeight: 600 })}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                            {renderAmount(totalIncome, { color: '#13c2c2', fontWeight: 600 })}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                            {renderAmount(totalExpense, { color: '#fa541c', fontWeight: 600 })}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                            {renderProfit(totalRevenue - totalExpense, totalRevenue)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                            {renderProfit(totalIncome - totalExpense, totalIncome)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} colSpan={trailingCellCount} />
                    </Table.Summary.Row>
                </Table.Summary>
            );
        };

        return (
            <div>
                {/* 부서 통계 */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} lg={10} xl={5}>
                        <Card>
                            <Statistic
                                title="수주목표금액"
                                value={departmentStat.orderTarget}
                                prefix={<TrophyOutlined />}
                                suffix="원"
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={10} xl={5}>
                        <Card>
                            <Statistic
                                title="수주추진금액"
                                value={departmentStat.planningAmount}
                                prefix={<RiseOutlined />}
                                suffix="원"
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={10} xl={4}>
                        <Card>
                            <Statistic
                                title="매출금액"
                                value={departmentStat.revenueAmount}
                                prefix={<DollarOutlined />}
                                suffix="원"
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={10} xl={5}>
                        <Card>
                            <Statistic
                                title="계획수주 달성률"
                                value={departmentStat.plannedAchievementRate}
                                prefix={parseFloat(departmentStat.plannedAchievementRate) >= 100 ? <RiseOutlined /> : 
                                        parseFloat(departmentStat.plannedAchievementRate) >= 80 ? <ProjectOutlined /> : <FallOutlined />}
                                suffix="%"
                                valueStyle={{ 
                                    color: parseFloat(departmentStat.plannedAchievementRate) >= 100 ? '#52c41a' : 
                                           parseFloat(departmentStat.plannedAchievementRate) >= 80 ? '#faad14' : '#ff4d4f'
                                }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={10} xl={3}>
                        <Card>
                            <Statistic
                                title="실제수주 달성률"
                                value={departmentStat.actualAchievementRate}
                                prefix={parseFloat(departmentStat.actualAchievementRate) >= 100 ? <RiseOutlined /> : 
                                        parseFloat(departmentStat.actualAchievementRate) >= 80 ? <CalendarOutlined /> : <FallOutlined />}
                                suffix="%"
                                valueStyle={{ 
                                    color: parseFloat(departmentStat.actualAchievementRate) >= 100 ? '#52c41a' : 
                                           parseFloat(departmentStat.actualAchievementRate) >= 80 ? '#faad14' : '#ff4d4f'
                                }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={4} xl={2}>
                        <Card style={{ 
                            backgroundColor: '#f8f9fa', 
                            border: '1px dashed #d9d9d9',
                            height: '100%'
                        }}>
                            <div style={{ 
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                minHeight: '94px'
                            }}>
                                <div style={{ fontSize: 20, marginBottom: 4 }}>
                                    {department.name === '엔지니어링사업부' ? '⚙️' :
                                     department.name === 'IoT사업부' ? '🔗' :
                                     department.name === 'SW사업부' ? '💻' :
                                     department.name === '기술연구소' ? '🔬' : '🏢'}
                                </div>
                                <div style={{ color: '#666', fontSize: 12, fontWeight: 'bold' }}>
                                    {department.name}
                                </div>
                                <div style={{ color: '#999', fontSize: 10 }}>
                                    {selectedYear}년 실적
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* 부서 수입/지출/수익 (전체현황 탭과 동일 구성) */}
                <Row gutter={16} style={{ marginBottom: expenseDetailVisible ? 16 : 24 }}>
                    <Col xs={24} sm={12} lg={12} xl={6}>
                        <Card size="small">
                            <Statistic
                                title="수입금액"
                                value={departmentStat.incomeAmount}
                                prefix={<DollarOutlined />}
                                suffix="원"
                                formatter={value => `${Math.round(value)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{ color: '#13c2c2', fontSize: 20 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={12} xl={6}>
                        {/* 지출은 합계만 표시하고, 클릭하면 항목별 상세를 펼친다 */}
                        <Card
                            size="small"
                            hoverable
                            onClick={() => setExpandedDeptExpenseCards(keys => toggleKey(keys, department.id))}
                            style={{ cursor: 'pointer' }}
                        >
                            <Statistic
                                title={
                                    <Space size={4}>
                                        지출
                                        {expenseDetailVisible
                                            ? <UpOutlined style={{ fontSize: 10 }} />
                                            : <DownOutlined style={{ fontSize: 10 }} />}
                                    </Space>
                                }
                                value={departmentStat.totalExpense}
                                prefix={<FallOutlined />}
                                suffix="원"
                                formatter={value => `${Math.round(value)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{ color: '#fa541c', fontSize: 20 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={12} xl={6}>
                        <Card size="small">
                            <Statistic
                                title={`수익 (매출금액 대비)${departmentStat.revenueAmount > 0 ? ` · ${formatRate(departmentStat.profitAmount / departmentStat.revenueAmount * 100)}` : ''}`}
                                value={departmentStat.profitAmount}
                                prefix={departmentStat.profitAmount >= 0 ? <RiseOutlined /> : <FallOutlined />}
                                suffix="원"
                                formatter={value => `${Math.round(value)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{
                                    color: departmentStat.profitAmount >= 0 ? '#52c41a' : '#ff4d4f',
                                    fontSize: 20
                                }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={12} xl={6}>
                        <Card size="small">
                            <Statistic
                                title={`수익 (수입금액 대비)${departmentStat.incomeAmount > 0 ? ` · ${formatRate(departmentStat.incomeProfitAmount / departmentStat.incomeAmount * 100)}` : ''}`}
                                value={departmentStat.incomeProfitAmount}
                                prefix={departmentStat.incomeProfitAmount >= 0 ? <RiseOutlined /> : <FallOutlined />}
                                suffix="원"
                                formatter={value => `${Math.round(value)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{
                                    color: departmentStat.incomeProfitAmount >= 0 ? '#52c41a' : '#ff4d4f',
                                    fontSize: 20
                                }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* 지출 상세 (지출 카드 클릭 시) */}
                {expenseDetailVisible && (
                    <Card size="small" style={{ marginBottom: 24, backgroundColor: '#fafafa' }}>
                        {renderExpenseDetail(departmentStat.expenseByCategory)}
                    </Card>
                )}

                {/* 프로젝트 현황 탭 */}
                <Card title={`${department.name} 프로젝트 현황`}>
                    <Tabs
                        defaultActiveKey="planning"
                        size="middle"
                        items={[
                            {
                                key: 'planning',
                                label: (
                                    <Space>
                                        <RiseOutlined style={{ color: '#faad14' }} />
                                        수주진행사업
                                        <Tag color="orange" size="small">
                                            {filterProjectsByPriority(departmentStat.planningProjects).length}
                                        </Tag>
                                    </Space>
                                ),
                                children: (
                                    <Table
                                        columns={projectColumns}
                                        dataSource={filterProjectsByPriority(departmentStat.planningProjects)}
                                        rowKey="id"
                                        size="small"
                                        pagination={false}
                                        scroll={{ x: 1450 }}
                                        locale={{ emptyText: '계획단계 프로젝트가 없습니다.' }}
                                    />
                                )
                            },
                            {
                                key: 'active',
                                label: (
                                    <Space>
                                        <ProjectOutlined style={{ color: '#52c41a' }} />
                                        진행사업
                                        <Tag color="green" size="small">
                                            {filterProjectsByPriority(departmentStat.activeProjects).length}
                                        </Tag>
                                    </Space>
                                ),
                                children: (
                                    <>
                                        <div style={{ textAlign: 'right', marginBottom: 8 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {EXPENSE_ATTRIBUTION_NOTE}
                                            </Text>
                                        </div>
                                        <Table
                                            columns={activeProjectColumns}
                                            dataSource={filterProjectsByPriority(departmentStat.activeProjects)}
                                            rowKey={activeRowKey}
                                            size="small"
                                            pagination={false}
                                            scroll={{ x: 2100 }}
                                            summary={renderActiveProjectSummary}
                                            expandable={{
                                                showExpandColumn: false,
                                                expandedRowKeys: expandedProjectRows,
                                                expandedRowRender: record => renderExpenseDetail(record.department_expense)
                                            }}
                                            locale={{ emptyText: '진행중인 프로젝트가 없습니다.' }}
                                        />
                                    </>
                                )
                            }
                        ]}
                    />
                </Card>
            </div>
        );
    };

    // 부서명으로 정렬 (한글 이름순)
    const sortedDepartments = [...departments].sort((a, b) => a.name.localeCompare(b.name));
    
    console.log('🏷️ 탭 생성용 정렬된 부서:', sortedDepartments.map(d => ({ id: d.id, name: d.name })));
    console.log('📊 통계 데이터 보유 부서:', departmentStats.map(d => ({ id: d.id, name: d.name })));

    // 탭 아이템 생성
    const tabItems = [
        {
            key: 'overview',
            label: '전체 현황',
            children: (
                <div>
                    {/* 전체 통계 */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} lg={10} xl={5}>
                            <Card>
                                <Statistic
                                    title="전체 수주목표"
                                    value={overallStats.totalOrderTarget}
                                    prefix={<TrophyOutlined />}
                                    suffix="원"
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={10} xl={5}>
                            <Card>
                                <Statistic
                                    title="전체 수주추진"
                                    value={overallStats.totalPlanningAmount}
                                    prefix={<RiseOutlined />}
                                    suffix="원"
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    valueStyle={{ color: '#faad14' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={10} xl={4}>
                            <Card>
                                <Statistic
                                    title="전체 매출"
                                    value={overallStats.totalRevenueAmount}
                                    prefix={<DollarOutlined />}
                                    suffix="원"
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={10} xl={5}>
                            <Card>
                                <Statistic
                                    title="계획수주 달성률"
                                    value={overallStats.overallPlannedRate}
                                    prefix={parseFloat(overallStats.overallPlannedRate) >= 100 ? <RiseOutlined /> : 
                                            parseFloat(overallStats.overallPlannedRate) >= 80 ? <ProjectOutlined /> : <FallOutlined />}
                                    suffix="%"
                                    valueStyle={{ 
                                        color: parseFloat(overallStats.overallPlannedRate) >= 100 ? '#52c41a' : 
                                               parseFloat(overallStats.overallPlannedRate) >= 80 ? '#faad14' : '#ff4d4f'
                                    }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={10} xl={3}>
                            <Card>
                                <Statistic
                                    title="실제수주 달성률"
                                    value={overallStats.overallActualRate}
                                    prefix={parseFloat(overallStats.overallActualRate) >= 100 ? <RiseOutlined /> : 
                                            parseFloat(overallStats.overallActualRate) >= 80 ? <CalendarOutlined /> : <FallOutlined />}
                                    suffix="%"
                                    valueStyle={{ 
                                        color: parseFloat(overallStats.overallActualRate) >= 100 ? '#52c41a' : 
                                               parseFloat(overallStats.overallActualRate) >= 80 ? '#faad14' : '#ff4d4f'
                                    }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={4} xl={2}>
                            <Card style={{ 
                                backgroundColor: '#f0f2f5', 
                                border: '1px dashed #d9d9d9',
                                height: '100%'
                            }}>
                                <div style={{ 
                                    textAlign: 'center', 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    minHeight: '94px'
                                }}>
                                    <FundOutlined style={{ fontSize: 20, color: '#999', marginBottom: 4 }} />
                                    <div style={{ color: '#999', fontSize: 12, fontWeight: 'bold' }}>
                                        전략회의
                                    </div>
                                    <div style={{ color: '#666', fontSize: 10 }}>
                                        {selectedYear}년 현황
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {/* 전체 수입/지출/수익 */}
                    <Row gutter={16} style={{ marginBottom: showOverallExpenseDetail ? 16 : 24 }}>
                        <Col xs={24} sm={12} lg={12} xl={6}>
                            <Card size="small">
                                <Statistic
                                    title="전체 수입금액"
                                    value={overallStats.totalIncomeAmount}
                                    prefix={<DollarOutlined />}
                                    suffix="원"
                                    formatter={value => `${Math.round(value)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    valueStyle={{ color: '#13c2c2', fontSize: 20 }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={12} xl={6}>
                            {/* 지출은 합계만 표시하고, 클릭하면 항목별 상세를 펼친다 */}
                            <Card
                                size="small"
                                hoverable
                                onClick={() => setShowOverallExpenseDetail(v => !v)}
                                style={{ cursor: 'pointer' }}
                            >
                                <Statistic
                                    title={
                                        <Space size={4}>
                                            전체 지출
                                            {showOverallExpenseDetail
                                                ? <UpOutlined style={{ fontSize: 10 }} />
                                                : <DownOutlined style={{ fontSize: 10 }} />}
                                        </Space>
                                    }
                                    value={overallStats.totalExpense}
                                    prefix={<FallOutlined />}
                                    suffix="원"
                                    formatter={value => `${Math.round(value)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    valueStyle={{ color: '#fa541c', fontSize: 20 }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={12} xl={6}>
                            <Card size="small">
                                <Statistic
                                    title={`전체 수익 (매출금액 대비)${overallStats.totalRevenueAmount > 0 ? ` · ${formatRate(overallStats.overallProfitRate)}` : ''}`}
                                    value={overallStats.totalProfit}
                                    prefix={overallStats.totalProfit >= 0 ? <RiseOutlined /> : <FallOutlined />}
                                    suffix="원"
                                    formatter={value => `${Math.round(value)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    valueStyle={{
                                        color: overallStats.totalProfit >= 0 ? '#52c41a' : '#ff4d4f',
                                        fontSize: 20
                                    }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={12} xl={6}>
                            <Card size="small">
                                <Statistic
                                    title={`전체 수익 (수입금액 대비)${overallStats.totalIncomeAmount > 0 ? ` · ${formatRate(overallStats.overallIncomeProfitRate)}` : ''}`}
                                    value={overallStats.totalIncomeProfit}
                                    prefix={overallStats.totalIncomeProfit >= 0 ? <RiseOutlined /> : <FallOutlined />}
                                    suffix="원"
                                    formatter={value => `${Math.round(value)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    valueStyle={{
                                        color: overallStats.totalIncomeProfit >= 0 ? '#52c41a' : '#ff4d4f',
                                        fontSize: 20
                                    }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* 전체 지출 상세 (전체 지출 카드 클릭 시) */}
                    {showOverallExpenseDetail && (
                        <Card size="small" style={{ marginBottom: 24, backgroundColor: '#fafafa' }}>
                            {renderExpenseDetail(overallStats.totalExpenseByCategory)}
                        </Card>
                    )}

                    {/* 부서별 현황 테이블 */}
                    <Card>
                        <Tabs
                            defaultActiveKey="order"
                            size="middle"
                            items={[
                                {
                                    key: 'order',
                                    label: (
                                        <Space>
                                            <TrophyOutlined style={{ color: '#1890ff' }} />
                                            부서별 수주 현황
                                        </Space>
                                    ),
                                    children: (
                                        <>
                                            <div style={{ textAlign: 'right', marginBottom: 8 }}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    매출 귀속부서 배분율이 반영된 금액입니다
                                                </Text>
                                            </div>
                                            <Table
                                                columns={overviewColumns}
                                                dataSource={[...departmentStats].sort((a, b) => a.name.localeCompare(b.name))}
                                                rowKey="id"
                                                loading={loading}
                                                pagination={false}
                                                scroll={{ x: 1000 }}
                                            />
                                        </>
                                    )
                                },
                                {
                                    key: 'profit',
                                    label: (
                                        <Space>
                                            <DollarOutlined style={{ color: '#52c41a' }} />
                                            부서별 수익 현황
                                        </Space>
                                    ),
                                    children: (
                                        <>
                                            <div style={{ textAlign: 'right', marginBottom: 8 }}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {EXPENSE_ATTRIBUTION_NOTE}
                                                </Text>
                                            </div>
                                            <Table
                                                columns={profitColumns}
                                                dataSource={[...departmentStats].sort((a, b) => a.name.localeCompare(b.name))}
                                                rowKey="id"
                                                loading={loading}
                                                pagination={false}
                                                scroll={{ x: 1000 }}
                                                summary={renderProfitSummary}
                                                expandable={{
                                                    showExpandColumn: false,
                                                    expandedRowKeys: expandedProfitRows,
                                                    expandedRowRender: record => renderExpenseDetail(record.expenseByCategory)
                                                }}
                                            />
                                        </>
                                    )
                                }
                            ]}
                        />
                    </Card>
                </div>
            ),
        },
        ...sortedDepartments.map(department => ({
            key: `dept-${department.id}`,
            label: department.name,
            children: createDepartmentTab(department),
        }))
    ];

    return (
        <div>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 24 
            }}>
                <Title level={2} style={{ margin: 0 }}>
                    <Space>
                        <FundOutlined />
                        전략회의 대시보드
                    </Space>
                </Title>
                <Space>
                    <Input
                        placeholder="프로젝트명 검색"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 200 }}
                        prefix={<SearchOutlined />}
                        allowClear
                    />
                    <Radio.Group
                        value={priorityFilter}
                        onChange={e => setPriorityFilter(e.target.value)}
                        buttonStyle="solid"
                        size="middle"
                    >
                        <Radio.Button value="all">전체</Radio.Button>
                        <Radio.Button value="priority">
                            <StarFilled style={{ color: '#ff4d4f', marginRight: 4 }} />
                            중점관리
                        </Radio.Button>
                        <Radio.Button value="normal">일반</Radio.Button>
                    </Radio.Group>
                    <Select
                        value={selectedYear}
                        onChange={setSelectedYear}
                        style={{ width: 120 }}
                    >
                        {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map(year => (
                            <Option key={year} value={year}>{year}년</Option>
                        ))}
                    </Select>
                    <Button 
                        icon={<CalendarOutlined />} 
                        onClick={() => setProjectCalendarVisible(true)}
                        type="default"
                    >
                        프로젝트 현황
                    </Button>
                    <Button 
                        icon={<CalendarOutlined />} 
                        onClick={fetchData}
                        loading={loading}
                    >
                        새로고침
                    </Button>
                </Space>
            </div>

            <Spin spinning={loading}>
                <Tabs
                    activeKey={activeTabKey}
                    onChange={setActiveTabKey}
                    items={tabItems}
                    size="large"
                />
            </Spin>

            {/* 프로젝트 설명 모달 */}
            <Modal
                title={`프로젝트 설명 - ${descriptionModal.title}`}
                open={descriptionModal.visible}
                onCancel={handleDescriptionModalClose}
                footer={[
                    <Button key="close" onClick={handleDescriptionModalClose}>
                        닫기
                    </Button>
                ]}
                width={600}
            >
                <div style={{ 
                    maxHeight: '400px', 
                    overflowY: 'auto', 
                    padding: '16px',
                    backgroundColor: '#fafafa',
                    borderRadius: '6px',
                    border: '1px solid #f0f0f0'
                }}>
                    <Text>{descriptionModal.content}</Text>
                </div>
            </Modal>

            {/* 프로젝트 달력 모달 */}
            <ProjectCalendarModal
                visible={projectCalendarVisible}
                onCancel={() => setProjectCalendarVisible(false)}
            />

            {/* 프로젝트 수정 모달 */}
            {selectedProjectId && (
                <ProjectEditModal
                    projectId={selectedProjectId}
                    visible={editModalVisible}
                    onSuccess={handleEditSuccess}
                    onCancel={handleEditCancel}
                />
            )}
        </div>
    );
};

export default StrategicMeetingPage;